import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsTaskingEnabledForAtLeastOneCareType } from 'src/store/careTypeFeatures';
import { selectFacilityId, selectSelectedShiftRid } from 'src/store/configSlice';
import { selectNowMs } from 'src/store/globalUISlice';
import { useGetShiftInstancesQuery } from 'src/store/sageAdminApi';

const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000;
const MINUTE_IN_MS = 1000 * 60;

const getCurrentMinuteTimestamp = () => Math.floor(Date.now() / MINUTE_IN_MS) * MINUTE_IN_MS;

const useShiftInstances = () => {
    const facilityRid = useSelector(selectFacilityId);
    const selectedShiftRid = useSelector(selectSelectedShiftRid);
    const isTaskingEnabled = useSelector(selectIsTaskingEnabledForAtLeastOneCareType);

    const shouldSkipQuery = !facilityRid || !isTaskingEnabled;

    const [currentTimeMs, setCurrentTimeMs] = useState(getCurrentMinuteTimestamp());

    // Kept separate from currentTimeMs so the shift-instances query cache key stays stable.
    const nowMs = useSelector(selectNowMs);

    const {
        data: shiftInstanceResponse,
        isLoading: isLoadingShifts,
        refetch: refetchShifts,
    } = useGetShiftInstancesQuery(
        { facilityRid: facilityRid, currentTimeMs: currentTimeMs },
        { skip: shouldSkipQuery }
    );

    const handleRefetchShifts = () => {
        setCurrentTimeMs(getCurrentMinuteTimestamp());
        refetchShifts();
    };

    const { shiftInstances = [] } = shiftInstanceResponse ?? {};
    const selectedShift = useMemo(() => {
        return shiftInstances.find((shift) => shift.id === selectedShiftRid);
    }, [shiftInstances, selectedShiftRid]);

    const { defaultShift, currentTimeShift } = useMemo(() => {
        if (!shiftInstances || shiftInstances.length === 0) {
            return { defaultShift: null, currentTimeShift: null };
        }

        // Sort valid shift instances by start time in descending order, handling undefined startTimeMs
        const sortedShiftInstances = [...shiftInstances].sort(
            (a, b) => (b.startTimeMs ?? 0) - (a.startTimeMs ?? 0)
        );

        const currentTimeShift =
            sortedShiftInstances.find(
                (shift) =>
                    shift.startTimeMs &&
                    shift.endTimeMs &&
                    shift.startTimeMs <= nowMs &&
                    nowMs <= shift.endTimeMs
            ) ?? null;

        const defaultShift =
            sortedShiftInstances.find(
                (shift) => shift.startTimeMs && shift.startTimeMs <= nowMs + THIRTY_MINUTES_IN_MS
            ) ?? null;

        return { defaultShift, currentTimeShift };
    }, [shiftInstances, nowMs]);

    // Compared against nowMs (not currentTimeShift) so users logging in 5 min early
    // see the future-shift banner before any shift becomes "current".
    const { isViewingOffShift, offShiftDirection } = useMemo<{
        isViewingOffShift: boolean;
        offShiftDirection?: 'past' | 'future';
    }>(() => {
        if (!selectedShift) return { isViewingOffShift: false };
        const start = selectedShift.startTimeMs ?? 0;
        const end = selectedShift.endTimeMs ?? 0;

        return nowMs < start
            ? { isViewingOffShift: true, offShiftDirection: 'future' }
            : nowMs > end
            ? { isViewingOffShift: true, offShiftDirection: 'past' }
            : { isViewingOffShift: false };
    }, [selectedShift, nowMs]);

    return {
        shiftInstances,
        isLoadingShifts,
        defaultShift,
        selectedShift,
        currentTimeShift,
        isViewingOffShift,
        offShiftDirection,
        handleRefetchShifts,
    };
};

export default useShiftInstances;
